import dcmjs from "dcmjs";
import * as cornerstone from "@cornerstonejs/core";

export interface SegInfo {
  labelmapBufferArray: ArrayBuffer[];
  segMetadata: {
    seriesInstanceUid: string;
    data: dcmjs.data.SegMetadata[];
  };
  segmentsOnFrame: number[][];
  segmentsOnFrameArray: number[][][];
}

// @ts-ignore
const { generateToolState } = dcmjs.adapters.Cornerstone.Segmentation;
// @ts-ignore
const { DicomMessage, DicomMetaDictionary, datasetToBuffer } = dcmjs.data;

function removeSourceImageSequence(arrayBuffer: ArrayBuffer) {
  const dicomData = DicomMessage.readFile(arrayBuffer);
  const dataset = DicomMetaDictionary.naturalizeDataset(dicomData.dict);
  // @ts-ignore
  dataset.SourceImageSequence = undefined;
  // @ts-ignore
  dataset._meta = DicomMetaDictionary.namifyDataset(dicomData.meta);
  const modifiedSEGBuffer = datasetToBuffer(dataset).buffer;
  return modifiedSEGBuffer;
}

/**
 * Process segmentation on main thread (no worker needed)
 * This is simpler but may cause brief UI freeze during parsing
 */
export async function getSegInfo(
  imageIds: string[],
  arrayBuffer: ArrayBuffer,
): Promise<SegInfo> {
  // Process synchronously on main thread
  const updatedSEGBuffer = removeSourceImageSequence(arrayBuffer);

  const { segMetadata, ...result } = generateToolState(
    imageIds,
    updatedSEGBuffer,
    cornerstone.metaData,
  );

  return {
    ...result,
    segMetadata: JSON.parse(JSON.stringify(segMetadata)),
  } as SegInfo;
}

export function injectBufferIntoSegVolume(
  segmentationVolume: cornerstone.ImageVolume,
  labelMap3DBuffer: ArrayBuffer,
) {
  const { dimensions } = segmentationVolume;

  const totalVoxels = dimensions[0] * dimensions[1] * dimensions[2];

  const sourceUnit16 = new Uint16Array(labelMap3DBuffer);
  const labelMapData = Uint8Array.from(sourceUnit16);

  // Build the complete label map array first

  let nonZeroCount = 0;
  for (let i = 0; i < labelMapData.length; i++) {
    if (labelMapData[i] > 0) nonZeroCount++;
  }

  // Method 1: Try voxelManager.setCompleteScalarDataArray (v4)
  // @ts-ignore
  if (segmentationVolume.voxelManager?.setCompleteScalarDataArray) {
    try {
      // @ts-ignore
      segmentationVolume.voxelManager.setCompleteScalarDataArray(labelMapData);

      return;
    } catch (e) {
      console.warn("setCompleteScalarDataArray failed:", e);
    }
  }

  // Method 2: Try getting writable scalar data from voxelManager
  // @ts-ignore
  if (segmentationVolume.voxelManager) {
    try {
      // @ts-ignore - Try to get the actual internal array
      let scalarData = segmentationVolume.voxelManager.scalarData;
      if (!scalarData) {
        // @ts-ignore
        scalarData =
          segmentationVolume.voxelManager.getCompleteScalarDataArray();
      }

      if (scalarData && scalarData.length === totalVoxels) {
        // Copy data
        for (let i = 0; i < totalVoxels; i++) {
          scalarData[i] = labelMapData[i];
        }

        // Mark as modified
        // @ts-ignore
        if (segmentationVolume.voxelManager.modified) {
          // @ts-ignore
          segmentationVolume.voxelManager.modified();
        }
        return;
      }
    } catch (e) {
      console.warn("voxelManager direct access failed:", e);
    }
  }

  // Method 3: Try imageData.getPointData().getScalars() (VTK way)
  // @ts-ignore
  if (segmentationVolume.imageData) {
    try {
      // @ts-ignore
      const pointData = segmentationVolume.imageData.getPointData();
      if (pointData) {
        const scalars = pointData.getScalars();
        if (scalars) {
          const scalarData = scalars.getData();
          if (scalarData && scalarData.length === totalVoxels) {
            for (let i = 0; i < totalVoxels; i++) {
              scalarData[i] = labelMapData[i];
            }
            // @ts-ignore
            segmentationVolume.imageData.modified();
            return;
          }
        }
      }
    } catch (e) {
      console.warn("imageData access failed:", e);
    }
  }

  // Method 4: Direct scalarData property (legacy)
  // @ts-ignore
  if (segmentationVolume.scalarData) {
    try {
      // @ts-ignore
      const scalarData = segmentationVolume.scalarData;
      if (scalarData.length === totalVoxels) {
        for (let i = 0; i < totalVoxels; i++) {
          scalarData[i] = labelMapData[i];
        }
        return;
      }
    } catch (e) {
      console.warn("direct scalarData access failed:", e);
    }
  }

  console.error("Failed to inject buffer - no valid method found");
  console.error("Volume keys:", Object.keys(segmentationVolume));
  // @ts-ignore
  console.error("voxelManager:", segmentationVolume.voxelManager);
}
